import { useState, useRef, useMemo, useEffect } from "react";
import { X } from "lucide-react";

const splitEmails = (v) => (v || "").split(",").map((s) => s.trim()).filter(Boolean);
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const merge = (existing, add) => {
  const seen = new Set();
  const out = [];
  [...splitEmails(existing), ...add].map((s) => s.trim()).filter(Boolean).forEach((e) => {
    const k = e.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(e); }
  });
  return out.join(", ");
};

// Campo destinatari in stile Outlook: ogni indirizzo è una "pillola" evidenziata e rimovibile,
// tutte visibili. Autocompletamento dalla rubrica. Il valore resta una stringa separata da virgola.
export const RecipientInput = ({ value, onChange, contacts = [], placeholder, testid }) => {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const chips = useMemo(() => splitEmails(value), [value]);
  const chosen = useMemo(() => new Set(chips.map((e) => e.toLowerCase())), [chips]);
  const token = draft.trim().toLowerCase();

  const suggestions = useMemo(() => {
    if (!token) return [];
    return contacts
      .filter((c) => !chosen.has((c.email || "").toLowerCase()))
      .filter((c) => (c.email || "").toLowerCase().includes(token) || (c.name || "").toLowerCase().includes(token))
      .slice(0, 8);
  }, [contacts, token, chosen]);

  useEffect(() => { setActive(0); }, [token]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const addEmail = (email) => {
    const e = (email || "").trim().replace(/,$/, "").trim();
    if (!e) return;
    onChange(merge(value, [e]));
    setDraft("");
    setOpen(false);
  };

  const removeChip = (email) => {
    onChange(chips.filter((c) => c.toLowerCase() !== email.toLowerCase()).join(", "));
    inputRef.current?.focus();
  };

  // Stile Outlook: cliccando su una pillola la si rende di nuovo modificabile nel campo di input.
  const editChip = (email) => {
    if (draft.trim()) addEmail(draft);
    onChange(chips.filter((c) => c.toLowerCase() !== email.toLowerCase()).join(", "));
    setDraft(email);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitDraft = () => { if (draft.trim()) addEmail(draft); };

  const onKeyDown = (e) => {
    if (open && suggestions.length > 0 && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setActive((a) => e.key === "ArrowDown" ? Math.min(a + 1, suggestions.length - 1) : Math.max(a - 1, 0));
      return;
    }
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        if (open && suggestions.length > 0) addEmail(suggestions[active].email);
        else addEmail(draft);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && !draft && chips.length > 0) {
      removeChip(chips[chips.length - 1]);
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (text && /[,;\n]/.test(text)) {
      e.preventDefault();
      onChange(merge(value, text.split(/[,;\n]+/)));
      setDraft("");
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1">
      <div
        data-testid={`${testid}-field`}
        onClick={() => inputRef.current?.focus()}
        className="min-h-9 w-full flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm cursor-text focus-within:border-[#007BFF] focus-within:ring-2 focus-within:ring-[#007BFF]/20 transition-colors"
      >
        {chips.length >= 5 && (
          <span data-testid={`${testid}-count`} className="inline-flex items-center shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
            {chips.length} destinatari
          </span>
        )}
        {chips.map((email, i) => {
          const valid = isValidEmail(email);
          return (
            <span
              key={email + i}
              data-testid={`${testid}-chip-${i}`}
              title={`${email} · clicca per modificare`}
              className={`inline-flex items-center gap-1 max-w-full pl-2 pr-1 py-0.5 rounded-full text-xs font-medium border transition-colors ${valid
                ? "bg-[#007BFF]/10 text-[#0056B3] border-[#007BFF]/30"
                : "bg-red-50 text-red-600 border-red-300"}`}
            >
              <button
                type="button"
                data-testid={`${testid}-chip-edit-${i}`}
                onClick={(e) => { e.stopPropagation(); editChip(email); }}
                className="truncate cursor-pointer hover:underline"
              >
                {email}
              </button>
              <button
                type="button"
                data-testid={`${testid}-chip-remove-${i}`}
                onClick={(e) => { e.stopPropagation(); removeChip(email); }}
                className="shrink-0 rounded-full hover:bg-black/10 p-0.5 transition-colors"
                aria-label={`Rimuovi ${email}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          data-testid={testid}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={commitDraft}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={chips.length === 0 ? placeholder : ""}
          autoComplete="off"
          className="flex-1 min-w-[120px] bg-transparent outline-none border-0 py-0.5 text-sm placeholder:text-gray-400"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div data-testid={`${testid}-suggestions`} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((c, i) => (
            <button
              key={c.email}
              type="button"
              data-testid={`${testid}-suggestion-${i}`}
              onMouseDown={(e) => { e.preventDefault(); addEmail(c.email); }}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 flex flex-col transition-colors duration-100 ${i === active ? "bg-[#007BFF]/10" : "hover:bg-gray-50"}`}
            >
              <span className="text-sm text-gray-800">{c.name || c.email}</span>
              <span className="text-[11px] text-gray-500">{c.email}{c.building_name ? ` · ${c.building_name}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
